import { Router } from '~/core';
import { Allocation } from './Allocation';
import { BaseModel } from './BaseModel';
import { Egg } from './Egg';
import { Features } from './Features';
import { Node } from './Node';
import { ServerTemplate } from './ServerTemplate';
import { ServerTransfer } from './ServerTransfer';
import { User } from './User';

export class Server extends BaseModel {
    // Mirrors the Server::STATE_* constants in App\Models\Server (the `installed` column).
    public static readonly STATE_NOT_INSTALLED = 0;
    public static readonly STATE_INSTALLED = 1;
    public static readonly STATE_FAILED_INSTALL = 2;
    public static readonly STATE_DEPLOYING_BACKUP = 3;
    public static readonly STATE_FAILED_REINSTALL = 4;

    public uuid = '';
    public uuidShort = '';
    public slug: string | null = null;
    public name = '';
    public description = '';
    public limits = {
        memory: -1,
        swap: -1,
        disk: -1,
        io: -1,
        cpu: -1,
        cpuPinning: [],
        cpuBurst: null as number | null,
        cpuPriority: null as number | null,
        cpuPeriod: null as number | null,
    };
    public featureLimits = {
        databases: -1,
        allocations: -1,
        // -1 until the server loads; null once loaded means an unlimited limit.
        backupMegabytes: -1 as number | null,
        backupCount: null as number | null,
    };
    public monitor = false;
    public supportOp = false;
    public isFavorite = false;
    public ipBanned = false;
    // The port players connect on, resolved by the backend from the egg's elected
    // display port. Equal to the primary allocation's port unless the egg elects
    // another (a query port used for direct connect).
    public displayPort = 0;

    // Admin only props
    public id = '';
    public legacyId: number | null = null;
    public externalId?: string;
    public startup = '';
    public image = '';
    public suspended = false;
    public installed = -1;
    public updating = false;
    public moving = false;
    public importing = false;
    public oomDisabled = false;
    public forceOutgoingIp = false;
    public useRamdiskTmpfs = true;
    public disableGameQueries = false;
    public serverTemplateId: string | null = null;
    public templateDiverged = false;

    public isSplit = false;
    public syncSubusers = false;
    public parentServer: { identifier: string; name: string } | null = null;
    public splits: {
        identifier: string;
        name: string;
        resources: { cpu: number; memory: number; disk: number; swap: number };
        minimum: { cpu: number | null; memory: number | null; disk: number | null };
    }[] = [];

    // Catalog-driven flat map of every build/limit field, keyed by camelCased field name.
    public build: Record<string, unknown> = {};

    public get egg(): Egg {
        return this.getRelationship('egg');
    }

    public get originalEgg(): Egg | null {
        return this.getRelationship('originalEgg') || null;
    }

    public get serverTemplate(): ServerTemplate | undefined {
        return this.getRelationship('serverTemplate');
    }

    public get node(): Node {
        return this.getRelationship('node');
    }

    /**
     * Whether this server's egg carries the given tag slug. Tags replace the
     * former nest shortName as the game identifiers that drive feature gating.
     */
    public hasTag(slug: string): boolean {
        return this.egg?.tags?.some((tag) => tag.slug === slug) ?? false;
    }

    /**
     * Whether this server is affected by any of the given tag slugs. A server is
     * affected when the slug is carried by its node OR its egg, mirroring how the
     * backend resolves tag-targeted announcements.
     */
    public matchesAnyTag(slugs: string[]): boolean {
        if (slugs.length === 0) {
            return false;
        }

        const wanted = new Set(slugs);

        return (this.node?.tags || []).some((tag) => wanted.has(tag.slug)) || (this.egg?.tags || []).some((tag) => wanted.has(tag.slug));
    }

    public get isLocked(): boolean {
        return this.installed !== 1 || this.moving || this.updating || this.importing;
    }

    public get installFailed(): boolean {
        return this.installed === Server.STATE_FAILED_INSTALL || this.installed === Server.STATE_FAILED_REINSTALL;
    }

    public get allocations(): Allocation[] {
        return this.getRelationship('allocations') || [];
    }

    public primaryAllocation(): Allocation {
        return this.allocations.find((a) => a.primary)!;
    }

    public get features(): Features {
        return this.getRelationship('features');
    }

    public get permissions(): string[] {
        return this.getRelationship('permissions') || [];
    }

    public getRouteName() {
        return 'server';
    }

    public getSlugID(): string | null {
        return this.slug;
    }

    public getRouteID(): string {
        // Both admin and client contexts now use uuidShort for routing
        return this.uuidShort;
    }

    public openConsolePopup() {
        const { href } = Router.resolve({
            name: 'server.console',
            params: { server: this.uuidShort },
        });

        window.open(href, 'Console', 'width=600,height=400');
    }

    /**
     * The address to hand players. Prefers the allocation sitting on the display
     * port so its subdomain or alias is used; a server provisioned before its egg
     * reserved that port owns only its primary, so the primary's host carries the
     * display port instead.
     */
    public get connectAddress(): string {
        const onDisplayPort = this.allocations.find((a) => a.port === this.displayPort);

        if (onDisplayPort) {
            return onDisplayPort.displayName();
        }

        const primary = this.allocations.find((a) => a.primary);

        if (!primary) {
            return '';
        }

        return this.displayPort > 0 ? `${primary.displayName(false)}:${this.displayPort}` : primary.displayName();
    }

    public get joinURL(): string | null {
        return this.egg?.steamJoinButton ? `steam://connect/${this.connectAddress}` : null;
    }

    public get txAdminURL() {
        if (this.hasTag('fivem')) {
            // FIXME this returns null sometimes
            const ip = this.allocations.find((a) => !a.primary)?.displayName();
            if (!ip) return null;

            return `http://${ip}`;
        }
    }

    public get user(): User {
        return this.getRelationship('user');
    }

    public get transfer(): ServerTransfer | null {
        return this.getRelationship('transfer') || null;
    }
}
